import { updateArrayItem } from './array';
import { gather } from './function';
import { isEmpty } from './poly';
import { isDefined } from './type';

type NoUndefinedField<T> = {
  [P in keyof T]-?: NoUndefinedField<NonNullable<T[P]>>;
};

/*
  Filter object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value). E.g.,
  filterObject({1: 2, 3: 4, 4: 10}, (k, v) => isOdd(k)) -> {1: 2, 3: 4}
*/
export const filterObject = <S extends string | number | symbol, T>(
  obj: Readonly<Record<S, T>>,
  fn: (k: S, v: T) => boolean,
) => Object.fromEntries(Object.entries(obj).filter(gather(fn)));

export const filterObjectByKey = <T extends Record<string, unknown>>(
  obj: T,
  fn: (k: string) => boolean,
) =>
  Object.fromEntries(Object.entries(obj).filter(([k]) => fn(k))) as Partial<T>;

/*
  Removes keys from `obj` whose values are null or undefined
*/
export function filterNulls<S>(obj: S | null | undefined): NoUndefinedField<S> {
  // @ts-expect-error TS(2345): Argument of type 'S | null | undefined' is not ass... Remove this comment to see the full error message
  return filterObject(obj, (_, v) => isDefined(v));
}

/*
  Map object `obj` by function `fn`. The function should be of arity 2
  and operate on (key, value), returning an array of length 2.
  mapObject({1: 2, 3: 4, 4: 10}, (k, v) => [k + 1, v + 1]) ->
  {2: 3, 4: 5, 5: 11}
*/
export const mapObject = <S extends string | number | symbol, T, U, V = S>(
  obj: Readonly<Record<S, T>>,
  fn: (k: S, v: T) => [S | V, U | null | undefined],
) => Object.fromEntries(Object.entries(obj).map(gather(fn)));

export function objectEmpty<T>(
  value: Record<string, T> | null | undefined,
): boolean {
  return !(value && typeof value === 'object' && Object.keys(value).length > 0);
}

/**
 @deprecated type checking is incomplete. Use custom functions instead.
  Update an item in a nested object. E.g., updateIn({a: {b: 2}}, ['a', 'b'], x => x + 1) => {a: {b: 3}}
  If any levels do not exist, objects will be created
  Does not modify the original object
 */
// @ts-expect-error TS(7023): 'updateIn' implicitly has return type 'any' becaus... Remove this comment to see the full error message
export function updateIn<T>(
  // TODO: Fix this the next time the file is edited.
  // oxlint-disable-next-line no-explicit-any
  object: Record<string, any>,
  accessor: readonly string[],
  operation: (arg0: T) => T,
) {
  if (isEmpty(accessor)) {
    // @ts-expect-error TS(2345): Argument of type 'Record<string, any>' is not assignable to parameter of type 'T'.
    return operation(object);
  }

  if (!object) {
    return updateIn({}, accessor, operation);
  }

  if (Array.isArray(object)) {
    // handle 'objects' and 'arrays' with separate update operations
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line no-explicit-any
    return updateArrayItem<Record<string, any>>(
      object,
      Number(accessor[0]),
      // @ts-expect-error TS(7015): Element implicitly has an 'any' type because index expression is not of type 'number'.
      updateIn(object[accessor[0]], accessor.slice(1), operation),
    );
  }

  return {
    ...object,
    [accessor[0]]: updateIn(object[accessor[0]], accessor.slice(1), operation),
  };
}
